import React, { FunctionComponent, useState } from 'react';
import DashboardLayout from '../../../components/layout/layout';
import { Alert, Box, Button, Grid, Typography } from '@mui/material';
import { StyledTextField } from '../../../components/ui/formInput';
import { useNavigate } from 'react-router-dom';
import { useSendCPAdminUserTempPassword } from '../../../hooks/api/user';
import TempPasswordIcon from '../../../assets/img/Temp Password.png';

type ValidateField = {
  invalid: boolean;
  error: string;
};

type ValidateFieldsState = {
  email: ValidateField;
};

const AdminTempPasswordCreate: FunctionComponent = () => {
  const navigate = useNavigate();
  const formValidation: ValidateFieldsState = {
    email: { invalid: false, error: '' }
  };
  const [validateFields, setValidateFields] = useState<ValidateFieldsState>(formValidation);
  const [email, setEmail] = useState("");
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>();
  const [ disabled, setDisabled ] = useState<boolean>(false);
  const [ tempPassword, setTempPassword ] = useState<string | undefined>(undefined);

  const { mutateAsync: sendTempPassword } = useSendCPAdminUserTempPassword();

  const isValidEmail = (email: string) => {
    const emailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,6}$/;
    return emailRegex.test(email);
  };

  const validateForm = () => {
    let valid = true;
    const errors = { ...validateFields };

    if (!email?.trim()) {
      errors.email = { invalid: true, error: 'Email is required' };
      valid = false;
    } else if (!isValidEmail(email)) {
      errors.email = { invalid: true, error: 'Please enter a valid email address' };
      valid = false;
    } else {
      errors.email = { invalid: false, error: '' };
    }

    setValidateFields(errors);
    return valid;
  };

  const handleAction = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setValidateFields(formValidation);
    if (!validateForm()) {
      return;
    }
    try {
      setDisabled(true);
      const result = await sendTempPassword({email: email});
      if (result?.status === 200) {
        // Extract temp password from response
        const responseData = result?.response?.data || result?.data;
        if (responseData?.tempPassword) {
          setTempPassword(responseData.tempPassword);
        }
        setSuccess(true);
        setDisabled(false);
      } else {
        setDisabled(false);
        setError('An error occurred during login. Please try again.');
        setTimeout(() => {
          setError('');
        }, 3000);
      }
    } catch (err: any) {
      setDisabled(false);
      if (err?.response?.data?.message) {
        setError(err.response.data.message);
      } else if (err?.message) {
        setError(err.message);
      } else {
        setError('An error occurred during login. Please try again.');
      }
    }
  }

  return (
    <>
      <DashboardLayout titleIcon={<img src={TempPasswordIcon} alt="Temp Password" style={{ width: 20, height: 25 }} />} title={"Temp Password"} initialBreadcrumbs={"Extranet"} levelBreadcrumbs={''} breadcrumbs={'Create Temp Password'} activeMenu={'admin'} containerWidth={'80%'}>
        <Typography variant="body1" mb={3}>
          Use this page to generate a temporary password for a customer or channel partner who is unable to receive a password via the email generated by the Forgot Password web page.
        </Typography>
        <Typography variant="body1" mb={3}>
          To allow authorized users access to this functionality, set the field named <b>Barks Admin = Y</b> (uppercase Y)
          for the authorized user in the Users table of Exlogons database in the SQL server.
        </Typography>

        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          Customer/CP Email Address
        </Typography>

        <StyledTextField
          label="Email"
          value={email}
          onChange={(e: any) => setEmail(e.target.value)}
          error={validateFields.email.invalid}
          helperText={validateFields.email.error}
        />
        
        <Grid size={12}>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
          {
            success && (
              <Alert severity="success" sx={{ mt: 2 }}>
                Temp password generated successfully!
                {tempPassword && (
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Your temporary password: <b>{tempPassword}</b>
                  </Typography>
                )}
              </Alert>
            )
          }
        </Grid>

        <Box sx={{ mt: 3, display: "flex", alignItems: "center", gap: 3 }}>
          <Button disabled={disabled} variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important', minWidth: '100px' }} onClick={handleAction}>
            SUBMIT
          </Button>
          <Button disabled={disabled} variant="contained" sx={{ backgroundColor: '#c3183c', padding: '6px 25px !important', minWidth: '100px' }} onClick={() => navigate(`/admin`)}>
            CANCEL
          </Button>
        </Box>
      </DashboardLayout>
    </>
  )
};

export default AdminTempPasswordCreate;